import React from 'react';

import { AttentionMessage } from '@eika/attention-message';
import { IconTypes } from '@eika/icon';
import { ErrorLabel } from '@eika/label';
import { Link, LinkType } from '@eika/link';

import ANALYTICS_CATEGORIES from '../../../../analytics/categories';
import { useConfig } from '../../../../defaultConfig';
import { useTransferAccountsContext } from '../../../../hooks/useAccountState';
import useTranslation from '../../../../hooks/useTranslation';
import { isLoanAccount } from '../../../../services/accountFilters';
import { AnalyticsEvents, trackEvent } from '../../../../services/analytics';
import { NoteTypes } from '../../../../translations/translationType';
import { AmountBox, AmountInput } from '../../../AmountBox';
import { getLoanArrearValue } from '../../../LoanList/loanUtils';
import { NoteField } from '../../../NoteField';
import { AccountFreeWithdrawalLabel } from '../../../shared/AccountFreeWithdrawalLabel';
import { FormFieldset } from '../../../shared/FormFieldset';
import { SelectList } from '../../../shared/SelectList';
import AccountField from '../../AccountField';
import { DownpaymentActionType } from '../form/actions';
import { isNoCoverage } from '../form/validate';
import FieldProps from './FieldProps';
import { MAX_DOWNPAYMENT_LENGTH } from './consts';

const DownpaymentMobileFields: React.FC<FieldProps> = ({
    form,
    locked,
    shouldFocusFirstElement,
    freeWithdrawals,
    allAccounts,
    validFromAccounts,
    invalidAccounts,
    accountState,
    loanAccount,
    selectLoanAccount,
    isFixedLoan,
}) => {
    const { featureToggles, paymentPageLink } = useConfig();
    const { state, errors, handleChange, mutation } = form;
    const translated = useTranslation();
    const { targetFromAccount, targetToAccount } = useTransferAccountsContext();

    const loanArrears = featureToggles.enableLoanArrears ? getLoanArrearValue(loanAccount?.balance) : undefined;

    return (
        <FormFieldset disabled={mutation.isPending}>
            <AmountBox isNoCoverage={isNoCoverage(state, targetFromAccount)} error={errors.amount} locked={locked}>
                <AmountInput
                    value={state.amount}
                    ariaLabel={translated.transferAmount}
                    onChange={(value) => handleChange({ type: DownpaymentActionType.AMOUNT, payload: value })}
                    locked={locked}
                    focusFirst={shouldFocusFirstElement}
                    currency={targetFromAccount?.currency}
                />
            </AmountBox>

            {loanArrears && (
                <AttentionMessage
                    className="down-payment__info-message"
                    type="attention"
                    icon={IconTypes.INFO_DYN}
                    text={translated.loans.arrearsWarning.description}
                    clickableItem={
                        <Link
                            linkType={LinkType.BUTTON_SMALL_NEGATIVE}
                            href={`${paymentPageLink?.url}?receiverAccountNumber=${loanAccount?.accountNumber.value}&amount=${loanArrears}`}
                        >
                            {translated.loans.arrearsWarning.buttonText}
                        </Link>
                    }
                />
            )}
            <SelectList>
                <li>
                    <AccountField
                        id="transferFromAccount"
                        placeholder={translated.transferFromAccount}
                        buttonLabel={translated.from}
                        title={translated.transferFromAccount}
                        account={targetFromAccount}
                        accountNumber={state.fromAccount}
                        accounts={validFromAccounts ?? []}
                        invalidAccounts={invalidAccounts}
                        error={errors.accountId}
                        onChange={(accountNumber) =>
                            handleChange({
                                type: DownpaymentActionType.FROM_ACCOUNT_SELECTED,
                                payload: accountNumber,
                            })
                        }
                        disabled={mutation.isPending}
                        isLoading={accountState.isLoading}
                        loadingError={accountState.error}
                        refresh={accountState.refetch}
                        showModal={state.showFromAccounts}
                        setShowModal={(showModal: boolean) => {
                            if (showModal)
                                trackEvent(
                                    AnalyticsEvents.DOWNPAYMENT,
                                    ANALYTICS_CATEGORIES.DOWNPAYMENT,
                                    'select_from_account',
                                );

                            handleChange({
                                type: showModal
                                    ? DownpaymentActionType.SHOW_FROM_ACCOUNTS
                                    : DownpaymentActionType.HIDE_FROM_ACCOUNTS,
                            });
                        }}
                        locked={locked}
                        bottomLabel={
                            freeWithdrawals.show ? (
                                <AccountFreeWithdrawalLabel freeWithdrawals={freeWithdrawals.data} />
                            ) : undefined
                        }
                    />
                </li>
                <li>
                    <AccountField
                        id="transferToAccount"
                        placeholder={translated.transferToAccount}
                        buttonLabel={translated.to}
                        title={translated.transferToAccount}
                        account={targetToAccount ?? loanAccount}
                        accountNumber={state.toAccount ?? loanAccount?.accountNumber}
                        accounts={allAccounts?.filter(isLoanAccount) ?? []}
                        onChange={selectLoanAccount}
                        disabled={mutation.isPending}
                        isLoading={accountState.isLoading}
                        loadingError={accountState.error}
                        refresh={accountState.refetch}
                        showModal={state.showToAccounts}
                        setShowModal={(show) => {
                            if (show)
                                trackEvent(
                                    AnalyticsEvents.DOWNPAYMENT,
                                    ANALYTICS_CATEGORIES.DOWNPAYMENT,
                                    'select_to_account',
                                );

                            handleChange({
                                type: show
                                    ? DownpaymentActionType.SHOW_TO_ACCOUNTS
                                    : DownpaymentActionType.HIDE_TO_ACCOUNTS,
                            });
                        }}
                        locked={locked}
                        showNegativeAmount
                        bottomLabel={
                            isFixedLoan ? <ErrorLabel text={translated.downpayment.cannotDoDownpayment} /> : undefined
                        }
                    />
                </li>
                <li>
                    <NoteField
                        note={state.note}
                        labelText={translated.amountNote[NoteTypes.MESSAGE].label}
                        placeholder={translated.amountNote[NoteTypes.MESSAGE].placeholder}
                        onChange={(value: string) => handleChange({ type: DownpaymentActionType.NOTE, payload: value })}
                        onShow={(show) => {
                            if (show) {
                                trackEvent(AnalyticsEvents.TRANSFER, ANALYTICS_CATEGORIES.DOWNPAYMENT, 'select_note');
                            }
                        }}
                        locked={locked}
                        noteMaxLength={MAX_DOWNPAYMENT_LENGTH}
                        forceDisableKid
                    />
                </li>
            </SelectList>
            <AttentionMessage
                className="down-payment__info-message"
                type="info"
                icon={IconTypes.INFO_DYN}
                text={translated.loans.infoTextDownPayment}
            />
        </FormFieldset>
    );
};

export default DownpaymentMobileFields;
